import { KeyboardEvent, useEffect, useState } from 'react';
import type { SeparatorProps } from 'react-resizable-layout';
import { Tooltip } from 'src/components';
import { Cue } from 'src/components/Cue';
import { ResizeHandle } from 'src/components/ResizeHandle';

const RESIZE_CUE_TIMEOUT_MS = 7000;

const RESIZE_KEYS = new Set(['ArrowUp', 'ArrowDown', 'Up', 'Down']);

const resizeHandleSx = { top: '-0.3125rem', zIndex: 2 };

interface ComposerResizeHandleProps {
  separatorProps: SeparatorProps;
  isDragging: boolean;
  isCueOpen: boolean;
  onCueDismiss: () => void;
  onRestoreAutoHeight: () => void;
  onKeyboardResize: () => void;
}

export const ComposerResizeHandle = ({
  separatorProps,
  isDragging,
  isCueOpen,
  onCueDismiss,
  onRestoreAutoHeight,
  onKeyboardResize,
}: ComposerResizeHandleProps) => {
  const [handleEl, setHandleEl] = useState<HTMLElement | null>(null);

  // wall-clock deadline with no event to hang it off, so a timer is the mechanism
  useEffect(() => {
    if (!isCueOpen) {
      return undefined;
    }
    const timer = setTimeout(onCueDismiss, RESIZE_CUE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isCueOpen, onCueDismiss]);

  // the library's own Enter and double-click jump back to the initial height instead
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onRestoreAutoHeight();
      return;
    }
    if (RESIZE_KEYS.has(event.key)) {
      onKeyboardResize();
    }
    separatorProps.onKeyDown?.(event as KeyboardEvent<HTMLHRElement>);
  };

  return (
    <>
      <Tooltip
        title={isDragging || isCueOpen ? '' : 'Drag to resize · Double-click to auto-fit'}
        placement="top"
      >
        <ResizeHandle
          ref={setHandleEl}
          axis="y"
          isDragging={isDragging}
          {...separatorProps}
          tabIndex={0}
          aria-label="Resize composer"
          onKeyDown={onKeyDown}
          onDoubleClick={onRestoreAutoHeight}
          sx={resizeHandleSx}
          data-testid="composer-resize-handle"
        />
      </Tooltip>
      <Cue
        open={Boolean(isCueOpen && handleEl)}
        anchorEl={handleEl}
        placement="top"
        data-testid="composer-resize-cue"
      >
        Double-click here to auto-fit again
      </Cue>
    </>
  );
};
